import ProductHeader from "@components/Header/ProductHeaderContent"
import Helmet from "@components/Helmet"
import Layout from "@components/Layout"
import ReviewsImageGalleryModal from "@components/Modal/ReviewsImageGalleryModal"
import ReviewsModal from "@components/Modal/ReviewsModal"
import ReviewSubmittedModal from "@components/Modal/ReviewSubmittedModal"
import WriteReviewModal from "@components/Modal/WriteReviewModal"
import ProductSelector from "@components/ProductSelector"
import VariantSelector from "@components/VariantSelector"
import {
  RECENTLY_VIEWED_PRODUCTS_KEY,
  SELECTED_PRODUCTS_KEY,
} from "@constants/storageKeys"
import { useBaseContext } from "@context/BaseContext"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import { useMarketPricing } from "@hooks/useMarketPricing"
import useMultiWarehouseInventory, {
  INVENTORY_ITEM_TRACKED,
} from "@hooks/useMultiWarehouseInventory"
import { Dispatch, RootState } from "@redux/store"
import { getProductImageAlt } from "@utils/altText"
import { decodeShopifyId } from "@utils/decodeShopifyId"
import { findBestCategory } from "@utils/findBestCategory"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import {
  formatContentfulVariant,
  getDecodedShopifyProductId,
} from "@utils/index"
import { DesktopOnly } from "@utils/MediaQuery"
import { getMarketAmount } from "@utils/pricing"
import { renderRichText } from "@utils/renderRichText"
import { emptyRichText } from "@utils/richText"
import { withFinePrint } from "@utils/richTextHelpers"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import {
  Content,
  Tab,
  TabContent,
  Tabs,
  TabsBlock,
  TabTypes,
} from "@views/common/ProductDetails/common"
import DetailsBlock from "@views/common/ProductDetails/DetailsBlock"
import ExploreBlock from "@views/common/ProductDetails/ExploreBlock"
import InstallationBlock from "@views/common/ProductDetails/InstallationBlock"
import ProductMetaData from "@views/common/ProductDetails/ProductMetaData"
import ResourcesBlock from "@views/common/ProductDetails/ResourcesBlock"
import ReviewsBlock from "@views/common/ProductDetails/ReviewsBlock"
import { useDiscountAndUpsellProducts } from "@views/hooks/useDiscountAndUpsellProducts"
import { useNotAvailableInCountry } from "@views/hooks/useNotAvailableInCountry"
import { usePdpAnalytics } from "@views/hooks/usePdpAnalytics"
import { usePdpAvailability } from "@views/hooks/usePdpAvailability"
import { usePdpShipping } from "@views/hooks/usePdpShipping"
import { usePdpVariantState } from "@views/hooks/usePdpVariantState"
import { useProductMeta } from "@views/hooks/useProductMeta"
import { useReviewImages } from "@views/hooks/useReviewImages"
import { gsap } from "gsap"
import _ from "lodash-es"
import React, { useRef, useState } from "react"
import { connect } from "react-redux"

import { useProductReviews } from "../../reviews/useProductReviews"
import ProductBlock from "./components/ProductBlock"

const SpecialOpsProductDetailsPage: any = ({
  addToCart,
  automaticDiscounts,
  cartLineItems,
  customerEmail,
  customerLocation,
  customerLocationFetched,
  data,
  discountedProductsData,
  discountedProductsInventoryData,
  fetchDiscountInventoryForProduct,
  location,
}): any => {
  // BaseContext
  const { data: baseContext } = useBaseContext()
  const globalData = baseContext?.globalData || { menuItems: [] }

  // Modal states
  const [tab, setTab] = useState<TabTypes>(TabTypes.Details)
  const [showReviews, setShowReviews] = useState<boolean>(false)
  const [showWriteReview, setWriteReview] = useState<boolean>(false)
  const [showReviewSubmitted, setReviewSubmitted] = useState<boolean>(false)
  const [showReviewsImageGallery, setShowReviewsImageGallery] =
    useState<boolean>(false)
  const [showProductSelectorConfig, setShowProductSelectorConfig] =
    useState<any>({})
  const [showProductSelector, setShowProductSelector] = useState<number>(-1)
  const [showVariantSelector, setShowVariantSelector] = useState<boolean>(false)

  // Product
  const contentfulProduct = _.get(data, "contentfulProduct")
  const productData = React.useMemo(
    () => ({
      ...contentfulProduct,
      variants: contentfulProduct.variants?.map(formatContentfulVariant),
    }),
    [contentfulProduct]
  )
  const shopifyProductId = _.get(
    productData,
    "shopifyProduct.shopifyProductId",
    ""
  )
  const productId = getDecodedShopifyProductId(shopifyProductId)

  // Geo-restricted products
  const { countryProhibitedProducts } = _.get(
    data,
    "contentfulGlobalSettings",
    {}
  )
  const {
    blocked: notAvailableInCustomerCountry,
    message: notAvailableInCustomerCountryText,
  } = useNotAvailableInCountry({
    customerCountryCode: customerLocation?.customerCountryCode,
    fetched: customerLocationFetched,
    productSlug: productData?.slug,
    rules: countryProhibitedProducts,
  })

  // Resolve all possible variants (base, automatic discount, upsell)
  const buysAutomaticDiscount = automaticDiscounts.some(
    ({ customerBuysProducts }) =>
      customerBuysProducts?.includes(decodeShopifyId(shopifyProductId))
  )
  const getsAutomaticDiscount = automaticDiscounts.some(
    ({ customerGetsProducts }) =>
      customerGetsProducts?.includes(decodeShopifyId(shopifyProductId))
  )
  const validUpsellVariants = React.useMemo(() => {
    if (productData.upsell && productData.upsellCallout) {
      return _.reject(
        productData.upsell.variants,
        (variant: any) => !variant.images
      )
    }

    return []
  }, [productData.upsell])

  const variants = React.useMemo(() => {
    return _.get(productData, "variants", [])
  }, [productData])
  const variantSkus = React.useMemo(
    () =>
      _.uniq(
        variants
          .filter((variant) => {
            return (
              !!variant.deviceSlug && !!variant.deviceName && !!variant.images
            )
          })
          ?.map((variant) => variant.sku)
      ),
    [variants]
  )
  const mergedVariantSkus = React.useMemo(() => {
    const toSkuArray = (arr?: any[]) =>
      (arr ?? [])
        .map((v) => (typeof v === "string" ? v : v?.sku))
        .filter(Boolean)

    // Note: validDiscountVariants removed - now fetched separately via fetchDiscountInventoryForProduct
    return _.uniq([
      ...toSkuArray(variantSkus),
      ...toSkuArray(validUpsellVariants),
    ])
  }, [variantSkus, validUpsellVariants])

  const { inventoryData } = useMultiWarehouseInventory(
    mergedVariantSkus,
    variantSkus
  )

  // Fetch market pricing data for all variants
  const { data: marketPricingData } = useMarketPricing(variantSkus)

  // Create pricing map for quick lookups
  const marketPricingMap = React.useMemo(() => {
    const map = new Map()
    marketPricingData.forEach((variant) => {
      map.set(variant.sku, variant.marketPrices)
    })
    return map
  }, [marketPricingData])

  // Merge market pricing into variants
  const variantsWithPricing = React.useMemo(() => {
    return variants.map((variant) => ({
      ...variant,
      marketPrices: marketPricingMap.get(variant.sku) || {},
    }))
  }, [variants, marketPricingMap])

  const {
    currentVariant,
    fallbackVariant,
    price,
    retailPrice,
    setActiveVariant,
    validVariantsByStock,
  } = usePdpVariantState({
    customerCountryCode: customerLocation?.customerCountryCode || "US",
    inventoryData,
    location,
    mode: "SOPDP",
    variants: variantsWithPricing,
  })

  // Availability and shipping
  const { availabilityData, shippingMessage } = usePdpAvailability({
    currentSku: currentVariant?.sku,
    currentVariant,
    customerCountryCode: customerLocation?.customerCountryCode,
    inventoryData,
    mode: "SOPDP",
  })

  const { shippingText } = usePdpShipping({
    availabilityIsAvailable: availabilityData?.isAvailable,
    currentSku: currentVariant?.sku,
    enableFenixEstimates: false,
    shippingMessage, // from usePdpAvailability
  })

  // Analytics hooks
  const inventoryLoaded =
    customerLocationFetched &&
    customerLocation &&
    inventoryData &&
    Object.keys(inventoryData)?.length > 0

  const { onAddProductToCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart } = useElevarEvents()

  usePdpAnalytics({
    availabilityData,
    currentVariant,
    customerCountryCode: customerLocation?.customerCountryCode || "US",
    customerEmail,
    inventoryLoaded,
    productData,
  })

  // Store recently viewed products
  React.useEffect(() => {
    const recentProducts = getLocalStorage(RECENTLY_VIEWED_PRODUCTS_KEY) || []
    const imageSrc = _.get(productData, "variants.0.images.0", "")
    const [cleanedSrc] = (imageSrc || "").split("?")
    const dataToStore = {
      compareAtPrice:
        getMarketAmount(
          currentVariant,
          customerLocation?.customerCountryCode || "US",
          "compareAtPrice"
        ) || undefined,
      image: `https:${cleanedSrc}`,
      marketPrices: {
        US: {
          compareAtPrice: null,
          price: {
            amount: getMarketAmount(currentVariant, "US", "price"),
            currencyCode: "USD",
          },
        },
      },
      price: getMarketAmount(currentVariant, "US", "price"),
      productName: productData?.productName,
      productSlug: productData?.slug,
    }

    try {
      writeLocalStorage(
        RECENTLY_VIEWED_PRODUCTS_KEY,
        _.slice(_.uniqBy([dataToStore, ...recentProducts], "productSlug"), 0, 6)
      )
    } catch (error: any) {
      if (error?.name === "QuotaExceededError") {
        try {
          writeLocalStorage(RECENTLY_VIEWED_PRODUCTS_KEY, [dataToStore])
        } catch (nestedError: any) {
          if (nestedError?.name === "QuotaExceededError") {
            console.error(
              "Local storage quota exceeded while trying to write recently viewed products."
            )
          }
        }
      }
    }
  }, [])

  // Fetch discount inventory for products relevant to this PDP
  React.useEffect(() => {
    if (productData?.contentful_id && discountedProductsData) {
      fetchDiscountInventoryForProduct(productData.contentful_id)
    }
  }, [
    productData?.contentful_id,
    discountedProductsData,
    fetchDiscountInventoryForProduct,
  ])

  // Attach analytics handlers to add to cart handler
  const onAddToCart = React.useCallback(() => {
    const variantId = currentVariant?.variantId
    if (!variantId) return

    const imageSrc = _.get(currentVariant, "images.0", "")
    const [cleanedSrc] = imageSrc.split("?")

    addToCart({
      cartProduct: {
        brand: _.get(productData, "brand.name"),
        builtInMagnet: _.get(productData, "builtInMagnet", false),
        bundleProduct: _.get(productData, "bundleProduct", false),
        color: currentVariant?.color,
        compareAtPrice: retailPrice,
        contentfulProductId: productData?.contentful_id,
        contentfulVariantId: currentVariant?.contentfulVariantId,
        customPrint: _.get(productData, "customPrint", false),
        device:
          _.get(currentVariant, "deviceName") ??
          _.get(productData, "device.name"),
        deviceType: _.get(productData, "device.type.name"),
        image: `https:${cleanedSrc}`,
        language: productData?.language,
        marketPrices: currentVariant?.marketPrices,
        price,
        productName: productData?.productName,
        productSlug: productData?.slug,
        productSubType: _.get(productData, "productSubType.slug"),
        shopifyProductId: _.get(productData, "shopifyProduct.shopifyProductId"),
        shopifyVariantId: variantId,
        size: currentVariant?.size,
        sku: currentVariant?.sku,
        variantDevice: currentVariant?.deviceName,
        variantName: currentVariant?.name,
      },
    })

    const gtmProduct: GTMProductProps = {
      brand: _.get(productData, "brand.name"),
      device:
        _.get(currentVariant, "deviceName") ??
        _.get(productData, "device.name"),
      deviceType: _.get(productData, "device.type.name"),
      price,
      productId: _.get(productData, "shopifyProduct.shopifyProductId"),
      productName: productData?.productName,
      quantity: 1,
      sku: currentVariant?.sku,
      variantName: currentVariant?.name,
    }

    onAddProductToCart(gtmProduct)

    const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
    const list =
      selectedProducts[_.get(productData, "shopifyProduct.shopifyProductId")] ||
      "direct"

    const elevarProduct: ElevarProductProps = {
      brand: "Urban Armor Gear",
      device:
        _.get(currentVariant, "deviceName") ??
        _.get(productData, "device.name"),
      deviceType: _.get(productData, "device.type.name"),
      image: `https:${cleanedSrc}`,
      list,
      price,
      productId: _.get(productData, "shopifyProduct.shopifyProductId"),
      productName: productData?.productName,
      quantity: "1",
      sku: currentVariant?.sku,
      variantId,
      variantName: currentVariant?.name,
    }

    elevarOnAddToCart(elevarProduct, list)
  }, [
    addToCart,
    productData,
    currentVariant,
    price,
    retailPrice,
    onAddProductToCart,
    elevarOnAddToCart,
  ])

  // Discounts and Upsells
  const {
    allDiscountProductsIndexIdMap,
    automaticDiscountData,
    discountProducts,
    upsellProduct,
  } = useDiscountAndUpsellProducts({
    addToCart,
    automaticDiscountData: globalData?.automaticDiscounts || [],
    automaticDiscounts,
    customerCountryCode: customerLocation?.customerCountryCode || "US",
    discountedProductsData,
    elevarOnAddToCart,
    mode: "SOPDP",
    onAddProductToCart,
    productData,
    shopifyProductId,
  })

  const availableSkusByRegion = React.useMemo(() => {
    const countryCode = customerLocation?.customerCountryCode || "US"
    const combinedInventoryData = {
      ...inventoryData,
      ...discountedProductsInventoryData,
    }
    const discountVariants = _.flatten(
      Object.values(discountedProductsData)?.map(({ variants }) => variants)
    )
    const upsellVariants = upsellProduct?.variants || []
    const allVariants = [...discountVariants, ...upsellVariants]
    const availableSkus: any = []

    allVariants.forEach(({ sku }) => {
      const tracked = _.get(
        combinedInventoryData,
        `${sku}.${INVENTORY_ITEM_TRACKED}`,
        true
      )

      if (
        tracked &&
        getAvailabilityByRegion(countryCode, sku, combinedInventoryData)
          .isAvailable
      ) {
        availableSkus.push(sku)
      }
    })

    return availableSkus
  }, [
    inventoryData,
    discountedProductsData,
    discountedProductsInventoryData,
    upsellProduct,
    customerLocationFetched,
    customerLocation,
  ])

  const bogoTypeByIndex = React.useMemo(() => {
    return automaticDiscounts?.map((ad) => {
      if (ad.customerBuysProducts?.includes(decodeShopifyId(shopifyProductId)))
        return "X"
      if (ad.customerGetsProducts?.includes(decodeShopifyId(shopifyProductId)))
        return "Y"
      return undefined
    })
  }, [automaticDiscounts, shopifyProductId])

  const openProductSelector = React.useCallback(
    (i, id) => {
      const parsedDiscountId = id.replace(
        "gid://shopify/DiscountAutomaticNode/",
        ""
      )
      const discountConfig = automaticDiscountData?.find(
        ({ discountId }) => discountId === parsedDiscountId
      )
      const bogoType = bogoTypeByIndex[i]
      const config = discountConfig ? { ...discountConfig } : {}
      if (bogoType) (config as any)._bogoType = bogoType
      setShowProductSelectorConfig(config)
      setShowProductSelector(i)
    },
    [
      bogoTypeByIndex,
      automaticDiscountData,
      setShowProductSelector,
      setShowProductSelectorConfig,
    ]
  )

  const closeProductSelector = React.useCallback(() => {
    setShowProductSelector(-1)
  }, [setShowProductSelector])

  const openVariantSelector = React.useCallback(() => {
    setShowVariantSelector(true)
  }, [setShowVariantSelector])

  const closeVariantSelector = React.useCallback(() => {
    setShowVariantSelector(false)
  }, [setShowVariantSelector])

  // Scroll handlers
  const headerRef = useRef<HTMLDivElement | null>(null)
  const onRequestScrollToTop = React.useCallback(() => {
    const html = document.getElementsByTagName("html")
    if (html && html[0]) gsap.to(html[0], { duration: 0.4, scrollTop: 0 })
  }, [])
  const onRequestScrollToReviews = React.useCallback(() => {
    const html = document.getElementsByTagName("html")
    const tabs = document.getElementById("tabs-block")
    if (tabs) gsap.to(html[0], { duration: 0.4, scrollTop: tabs?.offsetTop })
  }, [])

  // UI
  const getDescriptionWithTemplateStrings = () => {
    const rawDescription = _.get(
      productData,
      "productDescription",
      emptyRichText
    )
    let formattedRaw = rawDescription?.raw
    if (rawDescription?.raw?.indexOf("%DEVICE%") > -1) {
      if (currentVariant?.deviceName) {
        formattedRaw = rawDescription.raw.replace(
          /%DEVICE%/g,
          currentVariant.deviceName
        )
      } else {
        formattedRaw = rawDescription.raw.replaceAll(
          /%DEVICE%/g,
          productData?.device?.name
        )
      }
    }
    return { raw: formattedRaw }
  }
  const templatedDescription = getDescriptionWithTemplateStrings()

  const installationText = productData?.installationText
  const installationMedia = productData?.installationMedia
  const installationImage = productData?.installationImage
  const hasInstallationTab =
    !!installationText && (installationMedia || installationImage)
  const resourcesText =
    currentVariant?.resourcesText || productData?.resourcesText
  const resourcesMedia =
    currentVariant?.resourcesMedia || productData?.resourcesMedia
  const safetyText = productData?.safetyText
  const safetyMedia = productData?.safetyMedia
  const hasResourcesTab =
    !!resourcesText || resourcesMedia || !!safetyText || safetyMedia

  // Reviews
  const { avgRating, goodReview, numReviews, reviews } = useProductReviews(
    String(productId),
    { per_page: 20 }
  )
  const [initialReviewImage, setInitialReviewImage] = useState<any>(null)
  const reviewImages = useReviewImages(reviews)
  const openReviewsImageGallery = (id: any) => {
    setShowReviewsImageGallery(true)
    setInitialReviewImage(id)
  }
  const closeReviewsImageGallery = () => {
    setShowReviewsImageGallery(false)
    setInitialReviewImage(null)
  }

  const showDiscountBlock =
    (buysAutomaticDiscount || getsAutomaticDiscount) &&
    automaticDiscountData &&
    discountProducts &&
    discountProducts.find((discountProducts) => discountProducts?.length > 0)

  const showProp65Button =
    customerLocation?.customerStateCode === "CA" &&
    _.get(currentVariant, "showProp65Warning", false)

  // SEO
  const { productMetaData, seoDescription, seoKeywords, seoTitle } =
    useProductMeta({
      avgRating,
      contentfulProduct,
      customerCountryCode: customerLocation?.customerCountryCode || "US",
      goodReview,
      numReviews,
      productData,
    })

  // Maestra
  const stripGidString = (id: string) => {
    return id?.replace("gid://shopify/Collection/", "") ?? ""
  }
  const [maestraCategoryId, setMaestraCategoryId] = useState<string>("")
  React.useEffect(() => {
    const sku = currentVariant?.sku ?? ""
    const device = _.get(productData, "device.name") ?? ""
    const collections = _.get(inventoryData, `${sku}.collections`) ?? []

    if (!sku || !device || !collections.length) return

    const collection = findBestCategory(device, collections)
    const collectionId = stripGidString(collection?.match?.id ?? "")
    if (collectionId && collectionId !== maestraCategoryId) {
      setMaestraCategoryId(collectionId)
    }
  }, [productData, currentVariant, inventoryData])

  // Check if any variant of this product is in the cart
  const productVariantIds = React.useMemo(() => {
    return (
      productData?.variants?.map((v) => v.shopifyVariantId || v.variantId) || []
    )
  }, [productData])

  const isProductInCart = React.useMemo(() => {
    return cartLineItems.some(({ node }: any) =>
      productVariantIds.includes(node?.merchandise?.id)
    )
  }, [cartLineItems, productVariantIds])

  return (
    <Layout
      data={data}
      FixedHeaderComponent={
        <ProductHeader
          color={{
            hexString: _.get(currentVariant, "hexString"),
            name: _.get(currentVariant, "name"),
            swatchImage: _.get(currentVariant, "swatchImage"),
          }}
          deviceName={_.get(currentVariant, "deviceName")}
          isAvailable={availabilityData.isAvailable}
          notAvailable={productData?.notAvailable}
          notAvailableButtonText={productData?.notAvailableButtonText}
          notAvailableInCountry={notAvailableInCustomerCountry}
          onClickAddToCart={onAddToCart}
          onClickDevice={onRequestScrollToTop}
          outOfStockButtonText={productData?.outOfStockButtonText}
          productName={_.get(productData, "productName")}
          productPrice={price}
        />
      }
      fixedHeaderTopRef={headerRef}
      location={location}
      showFixedHeader
    >
      <Helmet
        description={seoDescription}
        image={productData?.images?.[0]?.file?.url}
        imageAlt={getProductImageAlt({
          colorName: currentVariant?.name,
          deviceName: productData?.device?.name,
          imageType: "gallery",
          productName: productData?.productName || "",
        })}
        keywords={seoKeywords}
        title={seoTitle}
        url={`https://www.urbanarmorgear.com/products/${productData?.slug}`}
      />
      <ProductMetaData {...productMetaData} />
      <Content>
        {currentVariant && (
          <ProductBlock
            addDiscountedProductToCart={addToCart}
            addToCart={onAddToCart}
            automaticDiscounts={automaticDiscounts}
            avgRating={avgRating}
            bogoTypeByIndex={bogoTypeByIndex}
            currentVariant={currentVariant}
            device={_.get(currentVariant, "deviceName")}
            discountProducts={discountProducts}
            discountProductsIdMap={allDiscountProductsIndexIdMap}
            fallbackVariant={fallbackVariant}
            globalData={globalData}
            headerRef={headerRef}
            imageGalleryMuxVideos={_.get(productData, "imageGalleryMuxVideos")}
            images={_.map(
              _.get(productData, "images", []),
              ({ file }) => file.url
            )}
            isAvailable={availabilityData.isAvailable}
            isProductInCart={isProductInCart}
            lifestyleMuxVideos={_.get(productData, "lifestyleMuxVideos")}
            loadingInventory={!inventoryLoaded}
            notAvailable={productData?.notAvailable}
            notAvailableButtonText={productData?.notAvailableButtonText}
            notAvailableInCountry={notAvailableInCustomerCountry}
            notAvailableInCountryText={notAvailableInCustomerCountryText}
            numReviews={numReviews}
            onClickRatings={(type) => {
              if (type === "mobile") {
                setShowReviews(true)
              } else {
                onRequestScrollToReviews()
                setTab(TabTypes.Reviews)
              }
            }}
            onlyShowUpsellIfProductIsInCart={
              productData?.onlyShowUpsellIfProductIsInCart || false
            }
            openProductSelector={openProductSelector}
            openVariantSelector={openVariantSelector}
            outOfStockButtonText={productData?.outOfStockButtonText}
            price={price}
            productName={_.get(productData, "productName")}
            relatedSeries={_.get(data, "relatedSeries.nodes", [])}
            retailPrice={retailPrice}
            series={_.get(productData, "series.name")}
            setActiveVariant={(index) => setActiveVariant(index)}
            shippingText={shippingText}
            showDiscountBlock={showDiscountBlock}
            slug={_.get(productData, "slug")}
            upsellCallout={productData.upsellCallout}
            upsellProduct={upsellProduct}
            variants={validVariantsByStock}
          />
        )}
        <TabsBlock id={"tabs-block"}>
          <Tabs>
            <Tab
              $active={tab === TabTypes.Details}
              onClick={() => {
                setTab(TabTypes.Details)
              }}
            >
              Details
            </Tab>
            <DesktopOnly>
              <Tab
                $active={tab === TabTypes.Reviews}
                onClick={() => {
                  setTab(TabTypes.Reviews)
                }}
              >
                Reviews
              </Tab>
            </DesktopOnly>
            {hasInstallationTab && (
              <Tab
                $active={tab === TabTypes.Installation}
                onClick={() => {
                  setTab(TabTypes.Installation)
                }}
              >
                Installation
              </Tab>
            )}
            {hasResourcesTab && (
              <Tab
                $active={tab === TabTypes.Resources}
                onClick={() => {
                  setTab(TabTypes.Resources)
                }}
              >
                Resources
              </Tab>
            )}
          </Tabs>
          <TabContent $active={tab === TabTypes.Details}>
            <DetailsBlock
              avgRating={avgRating}
              description={renderRichText(templatedDescription, withFinePrint)}
              deviceAnchorText={null}
              features={_.get(productData, "featuresBlock") ?? []}
              numReviews={numReviews}
              prop65Text={renderRichText(
                _.get(
                  data,
                  "contentfulGlobalSettings.prop65Text",
                  emptyRichText
                ),
                withFinePrint
              )}
              setShowReviews={(value: boolean) => setShowReviews(value)}
              showProp65Button={showProp65Button}
              specs={
                _.get(currentVariant, "specs.productSpecification") ??
                _.get(productData, "specs.productSpecification") ??
                []
              }
              specSheets={
                _.get(currentVariant, "specSheets") ??
                _.get(productData, "specSheets") ??
                []
              }
            />
          </TabContent>
          <DesktopOnly>
            <TabContent $active={tab === TabTypes.Reviews}>
              <ReviewsBlock
                avgRating={avgRating}
                numReviews={numReviews}
                onClickCount={() => {
                  if (reviewImages?.length > 0) {
                    openReviewsImageGallery(reviewImages[0].id)
                  }
                }}
                onClickImage={openReviewsImageGallery}
                reviewImages={reviewImages}
                reviews={reviews}
                setShowReviews={(value: boolean) => setShowReviews(value)}
                setWriteReview={(value: boolean) => setWriteReview(value)}
              />
            </TabContent>
          </DesktopOnly>
          {hasInstallationTab && (
            <TabContent $active={tab === TabTypes.Installation}>
              <InstallationBlock
                image={installationImage}
                media={installationMedia}
                text={
                  installationText
                    ? renderRichText(
                        installationText || emptyRichText,
                        withFinePrint
                      )
                    : undefined
                }
              />
            </TabContent>
          )}
          {hasResourcesTab && (
            <TabContent $active={tab === TabTypes.Resources}>
              {resourcesMedia && (
                <ResourcesBlock
                  media={resourcesMedia}
                  text={
                    resourcesText
                      ? renderRichText(
                          resourcesText || emptyRichText,
                          withFinePrint
                        )
                      : undefined
                  }
                />
              )}
              {safetyMedia && (
                <ResourcesBlock
                  media={safetyMedia}
                  text={
                    safetyText
                      ? renderRichText(
                          safetyText || emptyRichText,
                          withFinePrint
                        )
                      : undefined
                  }
                />
              )}
            </TabContent>
          )}
        </TabsBlock>
        <ExploreBlock cards={_.get(productData, "highlightCards", [])} />
        <div data-popmechanic-embed="52570" />
        <div
          data-popmechanic-argument={maestraCategoryId}
          data-popmechanic-embed="52571"
        />
        <ReviewsModal
          avgRating={avgRating}
          isOpen={showReviews}
          numReviews={numReviews}
          onClickWriteReview={() => {
            setShowReviews(false)
            setWriteReview(true)
            setReviewSubmitted(false)
          }}
          onRequestClose={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
          onRequestOpenReviewImageGallery={openReviewsImageGallery}
          productId={productId}
          reviews={reviews}
        />
        <WriteReviewModal
          backToReviews={() => {
            setShowReviews(true)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
          isOpen={showWriteReview}
          onRequestClose={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
          onSubmit={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(true)
          }}
          productId={productId}
          productTitle={_.get(productData, "productName")}
        />
        <ReviewSubmittedModal
          isOpen={showReviewSubmitted}
          onRequestClose={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
        />
        <ReviewsImageGalleryModal
          initialImage={initialReviewImage}
          isOpen={showReviewsImageGallery}
          onRequestClose={closeReviewsImageGallery}
          reviews={reviews}
        />
        {showDiscountBlock && (
          <ProductSelector
            addToCart={addToCart}
            availableVariants={availableSkusByRegion}
            closeOnAddToCart
            customerCountryCode={customerLocation.customerCountryCode || "US"}
            deal={
              showProductSelectorConfig[
                `pdpMessage${showProductSelectorConfig.bogoType}`
              ] || ""
            }
            header={
              showProductSelectorConfig[
                `pdpMessage${showProductSelectorConfig.bogoType}`
              ] || ""
            }
            isOpen={showProductSelector > -1}
            onRequestClose={closeProductSelector}
            products={
              showProductSelector > -1
                ? discountProducts[showProductSelector]
                : []
            }
          />
        )}
        {!showDiscountBlock && upsellProduct && (
          <VariantSelector
            addToCart={addToCart}
            availableVariants={availableSkusByRegion}
            isOpen={showVariantSelector}
            onRequestClose={closeVariantSelector}
            product={productData.upsell}
          />
        )}
      </Content>
    </Layout>
  )
}

const mapState = (state: RootState) => ({
  automaticDiscounts: state.discounts.automaticDiscounts,
  cartLineItems: state.checkout.data?.lines?.edges || [],
  customerEmail: state.customer.data?.emailAddress?.emailAddress || "",
  customerLocation: state.customer.customerLocation,
  customerLocationFetched: state.customer.customerLocationFetched,
  discountedProductsData: state.discounts.discountedProductsData,
  discountedProductsInventoryData:
    state.discounts.discountedProductsInventoryData,
})

const mapDispatch = (dispatch: Dispatch) => ({
  addToCart: dispatch.checkout.addToCart,
  fetchDiscountInventoryForProduct:
    dispatch.discounts.fetchDiscountInventoryForProduct,
})

export default connect(mapState, mapDispatch)(SpecialOpsProductDetailsPage)
